import { useContext, useEffect, useRef, useState } from "react";
import dayjs from "@lib/dayjs";
import { deleteInterview, listInterview } from "@endpoints/interview.service";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { useNavigate } from "react-router-dom";
import { isObjectEmpty } from "@utils/helperFunction";
import { IListInterviewResponse } from "@endpoints/types/interview.type";
import { CalendarContext } from "../MeetingLayout";

export function formatEvents(data) {
  let formattedEvents = {};

  data?.forEach((item) => {
    const { specificSlot, slotSelectionType, ...rest } = item;

    if (slotSelectionType === "specific") {
      const dateString = dayjs(item?.specificSlot?.endTime).format("YYYY-MM");
      const slotData = {
        ...rest,
        slotSelectionType,
        startTime: specificSlot?.startTime,
        endTime: specificSlot?.endTime,
      };
      formattedEvents[dateString] = [
        ...(formattedEvents[dateString] || []),
        slotData,
      ];
    } else if (item?.generalAvailabilitySlots?.length) {
      item.generalAvailabilitySlots.forEach((generalSlot) => {
        const dateString = dayjs(generalSlot?.endTime).format("YYYY-MM");
        const slotData = {
          ...rest,
          slotSelectionType,
          startTime: generalSlot?.startTime,
          endTime: generalSlot?.endTime,
        };
        formattedEvents[dateString] = [
          ...(formattedEvents[dateString] || []),
          slotData,
        ];
      });
    }
  });

  //Sort the keys and return a new object
  const sortedEvents = Object.keys(formattedEvents)
    .sort() // ascending order by date string
    .reduce((acc, key) => {
      acc[key] = formattedEvents[key];
      return acc;
    }, {});

  return sortedEvents;
}

// export function groupedGeneralMeetings(data) {
//   return data.reduce((acc, meeting) => {
//     const meetingDate = meeting?.generalAvailabilitySlots[0]?.startTime;
//     const year = dayjs(meeting?.createdAt).format("YYYY");
//     const month = dayjs(meeting?.createdAt).format("MMMM");

//     if (!acc[year]) {
//       acc[year] = {};
//     }
//     if (!acc[year][month]) {
//       acc[year][month] = [];
//     }
//     acc[year][month].push(meeting);

//     return acc;
//   }, {});
// }

// export function groupedGeneralMeetings(data: IListInterviewResponse[]) {
//   const grouped = data.reduce((acc, meeting) => {
//     const year = dayjs(meeting?.createdAt).format("YYYY");
//     const month = dayjs(meeting?.createdAt).format("MMMM");

//     if (!acc[year]) acc[year] = {};
//     if (!acc[year][month]) acc[year][month] = [];

//     acc[year][month].push(meeting);

//     return acc;
//   }, {});

//   // sort each month's array
//   Object.keys(grouped).forEach((year) => {
//     Object.keys(grouped[year]).forEach((month) => {
//       grouped[year][month].sort((a, b) =>
//         dayjs(b.createdAt).diff(dayjs(a.createdAt))
//       );
//     });
//   });

//   return grouped;
// }


// export function groupedGeneralMeetings(data: IListInterviewResponse[]) {
//   const now = dayjs().startOf("month");

//   const grouped: Record<string, Record<string, IListInterviewResponse[]>> = {};

//   // 1️⃣ Group existing data
//   data.forEach((meeting) => {
//     const date = dayjs(meeting.createdAt);
//     const year = date.format("YYYY");
//     const month = date.format("MMMM");

//     if (!grouped[year]) grouped[year] = {};
//     if (!grouped[year][month]) grouped[year][month] = [];

//     grouped[year][month].push(meeting);
//   });

//   // 2️⃣ Ensure 12 future months exist (including current)
//   for (let i = 0; i < 12; i++) {
//     const future = now.add(i, "month");
//     const year = future.format("YYYY");
//     const month = future.format("MMMM");

//     if (!grouped[year]) grouped[year] = {};
//     if (!grouped[year][month]) grouped[year][month] = [];
//   }

//   // 3️⃣ Sort meetings inside each month (newest → oldest)
//   Object.keys(grouped).forEach((year) => {
//     Object.keys(grouped[year]).forEach((month) => {
//       grouped[year][month].sort((a, b) =>
//         dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
//       );
//     });
//   });

//   // 4️⃣ Rebuild sorted object (years DESC, months DESC)
//   const sortedGrouped: typeof grouped = {};

//   const sortedYears = Object.keys(grouped).sort(
//     (a, b) => Number(b) - Number(a) // newest year first
//   );

//   sortedYears.forEach((year) => {
//     const months = Object.keys(grouped[year]);

//     const sortedMonths = months.sort((a, b) => {
//       const dateA = dayjs(`${year}-${a}-01`, "YYYY-MMMM-DD");
//       const dateB = dayjs(`${year}-${b}-01`, "YYYY-MMMM-DD");
//       return dateB.valueOf() - dateA.valueOf(); // newest month first
//     });

//     sortedGrouped[year] = {};

//     sortedMonths.forEach((month) => {
//       sortedGrouped[year][month] = grouped[year][month];
//     });
//   });

//   return sortedGrouped;
// }

export function groupedGeneralMeetings(data: IListInterviewResponse[]) {
  const now = dayjs();
  // Using a Map for temporary grouping to preserve insertion order better than a plain object
  const tempGrouped: Record<string, IListInterviewResponse[]> = {};

  // 1️⃣ Generate the keys for the next 12 months (Newest to Oldest)
  // We do this first so empty months are placed in the correct chronological order
  for (let i = 11; i >= 0; i--) {
    const monthDate = now.add(i, "month");
    const key = monthDate.format("MMMM YYYY"); // e.g., "March 2026"
    tempGrouped[key] = [];
  }

  // 2️⃣ Group existing data into those months
  data.forEach((meeting) => {
    const extractedDateTime = meeting?.slotSelectionType === 'general'? meeting?.generalAvailabilitySlots[0]?.startTime: meeting?.specificSlot?.startTime
    const meetingDate = dayjs(extractedDateTime);
    const key = meetingDate.format("MMMM YYYY");

    // If the meeting is older than our 12-month window, create the key dynamically
    if (!tempGrouped[key]) {
      tempGrouped[key] = [];
    }
    tempGrouped[key].push(meeting);
  });

  // 3️⃣ Transform into final Array structure and Sort
  const result = Object.entries(tempGrouped)
    .map(([title, meetings]) => ({
      title, // e.g., "November 2025"
      data: meetings.sort((a, b) => 
        dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
      ),
    }))
    .sort((a, b) => {
      // Final sort to ensure Month/Year sequence is strictly Newest -> Oldest
      return dayjs(b.title, "MMMM YYYY").valueOf() - dayjs(a.title, "MMMM YYYY").valueOf();
    });

  return result;
}


const ScheduleCard = ({ item, index }) => {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/meeting-poll-details?id=${item?._id}`)}
      className={`flex items-center px-4 cursor-pointer hover:bg-gray-50`}
    >
      <div className="w-[26%]">
        <span className={`font-medium text-light-black text-xs`}>
          {dayjs(item?.createdAt).format("Do")}&nbsp;&nbsp;&nbsp;
          {dayjs(item?.createdAt).format("dddd")}{" "}
          {dayjs(item?.createdAt).format("hh:mm a")}
        </span>
      </div>
      <div
        className={`flex-1 grid grid-cols-[1fr_1.25fr_1fr_1fr] ${index !== 0 ? "border-t" : ""
          } py-4 justify-between items-center`}
      >
        <div className="flex flex-1 items-center">
          <span className="font-semibold text-xs text-primary text-center flex-1">
            Poll
          </span>
        </div>
        <div className="flex flex-1 items-center">
          <span className="font-medium text-xs text-light-black flex-1">
            {item?.meetingTitle}
          </span>
        </div>
        {item?.interviewStatus === "Pending Acceptance" ? (
          <div className="flex flex-1 items-center bg-light-black rounded-sm py-1 w-max px-3">
            <span className="font-semibold text-xs text-white text-center flex-1">
              Pending Acceptance
            </span>
          </div>
        ) : (
          <div className="flex flex-1 items-center gap-2">
            {["A", "B", "C"]?.map((item) => {
              return (
                <div className="flex items-center justify-center h-8 w-8 border rounded-full border-light-black text-light-black">
                  {item}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex flex-1 items-center">
          <span className="font-medium text-xs text-light-black flex-1">
            {item?.createdBy?.firstName} {item?.createdBy?.lastName}
          </span>
        </div>
      </div>
    </div>
  );
};

const SchedulingSection = () => {
  const {
    events,
    setEvents,
    showCreateEventFormModal,
    setShowCreateEventFormModal,
  } = useContext(CalendarContext);
  const itemRefs = useRef([])
  const [isEventLoading, setIsEventLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [schedulingPolls, setSchedulingPolls] = useState([]);

  useEffect(() => {
    if(schedulingPolls?.length){
      const title = dayjs().format('MMMM YYYY')
      itemRefs.current[title]?.scrollIntoView({
        behavior: "smooth",
        block: 'center',
      });
    }
  }, [schedulingPolls])

  useEffect(() => {
    listInterview({
      fetchGeneralSlotInterviews: true,
    })
      .then((response) => {
        const { success, data } = response;
        if (success) {
          // data?.map((item) => {
          //   deleteInterview(item?._id);
          // });
          setSchedulingPolls(() => groupedGeneralMeetings(data));
          console.log('groupedGeneralMeetings(data)',groupedGeneralMeetings(data))
          // setEvents(() => formatEvents(data));
        }
      })
      .finally(() => setIsEventLoading(false));
  }, []);

  useEffect(() => {
    return () => {
      if (showCreateEventFormModal) {
        setEditingEvent(null);
      }
    };
  }, [showCreateEventFormModal]);

  console.log("event from api", events);
  // console.log("Editing event", editingEvent);
  return (
    <div className="items-center w-full">
      <div className="grid grid-cols-[2fr_1fr_0.8fr_0.8fr] w-full border-b p-3 mb-4">
        <span className="font-semibold text-xs text-gray-500">
          Last updated
        </span>
        <span className="font-semibold text-xs text-gray-500">Poll title</span>
        <span className="font-semibold text-xs text-gray-500">Pending</span>
        <span className="font-semibold text-xs text-gray-500">Created By</span>
      </div>

      {isEventLoading ? (
        <div className="flex items-center justify-center flex-1 flex-col">
          <LoadingAnimation color="#2e2e2e" />
        </div>
      ) : isObjectEmpty(schedulingPolls) ? (
        <div className="font-medium text-light-black text-sm h-[calc(100vh-300px)] flex flex-col justify-center items-center">
          No scheduling poll found.
        </div>
      ) : (
        // <div className="flex-grow">
        //   {Object.keys(schedulingPolls).map((year, yearIndex) => (
        //     <div key={year} className="flex flex-col gap-10">
        //       {Object.keys(schedulingPolls[year]).map((month, monthIndex) => (
        //         <div key={month}>
        //           <div
        //             className={`flex items-center py-1.5 px-3 gap-4 bg-[#F1F5F9] rounded-sm`}
        //           >
        //             <label className="font-medium text-xs">
        //               {month} {year}
        //             </label>
        //           </div>
        //           {/* List of scheduled meetings */}
        //           <div className="flex flex-col mt-4">
        //             {schedulingPolls[year][month].map((meeting, index) => (
        //               <ScheduleCard key={index} item={meeting} index={index} />
        //             ))}
        //           </div>
        //         </div>
        //       ))}
        //     </div>
        //   ))}
        // </div>
        <div
        className="flex flex-col gap-7">
        {
          schedulingPolls?.map((item) => {
            return <div key={item?.title}
              ref={(el) => {
                if (el) itemRefs.current[item?.title] = el
              }}
              className="flex flex-col">
              <div
                className={`flex items-center py-1.5 px-3 gap-4 bg-[#F1F5F9] rounded-sm`}
              >
                <label className="font-medium text-xs">{item?.title}</label>
              </div>
              {!item?.data?.length ?
                <span className="text-xs font-medium m-auto mt-4 text-light-black">No meeting scheduled</span>
                :
                item?.data.map((meeting, meetingIndex) => (
                  <ScheduleCard key={meeting?._id} item={meeting} index={meetingIndex} />
                ))
              }
            </div>
          })
        }
      </div>
      )}
    </div>
  );
};

export default SchedulingSection;
